// Generic React error boundary for the dashboard. Mounted inside the
// AppShell with `key={location.pathname}` so it auto-resets on every
// navigation — a stuck error on /usage doesn't poison /agents the
// next time the user clicks.
//
// Why a class component: React's error-boundary contract
// (componentDidCatch + getDerivedStateFromError) only works on classes.
// Functional components with hooks cannot catch render errors. This is
// the smallest class we ever write in this app.
//
// Logging: the boundary writes to console.error in dev. Sentry hook
// goes here once the SDK is wired (Phase 0 plan §47); the call site
// is the one place that sees both the error and the React component
// stack, so it's the ideal capture point.

import * as React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { Button } from './ui/button.js';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
     
    console.error('[dashboard] uncaught render error', error, info.componentStack);
  }

  private readonly retry = (): void => {
    this.setState({ error: null });
  };

  override render(): React.ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="mx-auto max-w-xl py-8" role="alert">
        <Card>
          <CardHeader>
            <CardTitle>Something went wrong</CardTitle>
            <CardDescription>
              An unexpected error broke this page. Try again, or head back to the home dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs text-muted-foreground">
              {this.state.error.message}
            </pre>
            <div className="flex gap-2">
              <Button onClick={this.retry}>Try again</Button>
              <Button variant="outline" asChild>
                <Link to="/">Go home</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}
